/**
 * Heidi Loves Pets — Shopify OS 2.0 Theme
 * theme.js — Main Theme JavaScript
 * Vanilla JS only. No jQuery.
 */

(function () {
  'use strict';

  /* =========================================================================
     1. Sticky Header
     ========================================================================= */
  (function initStickyHeader() {
    const header = document.getElementById('site-header');
    if (!header) return;

    const SCROLL_THRESHOLD = 10;
    let ticking = false;

    function onScroll() {
      if (!ticking) {
        window.requestAnimationFrame(function () {
          if (window.scrollY > SCROLL_THRESHOLD) {
            header.classList.add('scrolled');
          } else {
            header.classList.remove('scrolled');
          }
          ticking = false;
        });
        ticking = true;
      }
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    // Run once on load in case page is already scrolled
    onScroll();
  })();

  /* =========================================================================
     2. Mobile Menu Toggle
     ========================================================================= */
  (function initMobileMenu() {
    const hamburger = document.querySelector('.hamburger');
    const menu      = document.querySelector('.mobile-menu');
    if (!hamburger || !menu) return;

    const focusableSelectors = [
      'a[href]',
      'button:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])',
    ].join(', ');

    function openMenu() {
      menu.classList.add('open');
      hamburger.setAttribute('aria-expanded', 'true');
      document.body.style.overflow = 'hidden';

      // Move focus to first focusable element inside menu
      const firstFocusable = menu.querySelector(focusableSelectors);
      if (firstFocusable) firstFocusable.focus();
    }

    function closeMenu() {
      menu.classList.remove('open');
      hamburger.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
      // CRITICAL: restore focus to the hamburger button
      hamburger.focus();
    }

    function toggleMenu() {
      const isOpen = menu.classList.contains('open');
      if (isOpen) {
        closeMenu();
      } else {
        openMenu();
      }
    }

    hamburger.addEventListener('click', toggleMenu);

    // Close on ESC key
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && menu.classList.contains('open')) {
        closeMenu();
      }
    });

    // Close when any link inside menu is clicked
    menu.querySelectorAll('a').forEach(function (link) {
      link.addEventListener('click', closeMenu);
    });

    // Expose globally so other modules can close the menu
    window.__hlp = window.__hlp || {};
    window.__hlp.closeMenu = closeMenu;
  })();

  /* =========================================================================
     3. Announcement Bar Dismiss
     ========================================================================= */
  (function initAnnouncementBar() {
    const bar        = document.querySelector('.announcement-bar');
    const closeBtn   = bar && bar.querySelector('.announcement-bar__close');
    if (!bar) return;

    const STORAGE_KEY = 'announcementDismissed';

    // If already dismissed in this session, hide immediately
    if (sessionStorage.getItem(STORAGE_KEY) === '1') {
      bar.setAttribute('data-dismissed', 'true');
      return;
    }

    if (!closeBtn) return;

    closeBtn.addEventListener('click', function () {
      bar.setAttribute('data-dismissed', 'true');
      sessionStorage.setItem(STORAGE_KEY, '1');
    });
  })();

  /* =========================================================================
     4. Cart Badge Update
     Listens for the custom 'cart:updated' event dispatched by cart-drawer.js
     ========================================================================= */
  (function initCartBadge() {
    document.addEventListener('cart:updated', function (e) {
      const count   = e.detail && typeof e.detail.count === 'number' ? e.detail.count : 0;
      const badges  = document.querySelectorAll('.cart-badge');

      badges.forEach(function (badge) {
        badge.textContent       = count > 0 ? (count > 99 ? '99+' : String(count)) : '';
        badge.dataset.count     = String(count);
        badge.hidden            = count === 0;
      });
    });
  })();

  /* =========================================================================
     5. Search Drawer Toggle
     ========================================================================= */
  (function initSearchDrawer() {
    const searchToggles = document.querySelectorAll('[data-search-toggle]');
    const searchDrawer  = document.querySelector('.search-drawer');
    const searchInput   = searchDrawer && searchDrawer.querySelector('input[name="q"]');
    if (!searchDrawer) return;

    function openSearch() {
      searchDrawer.classList.add('open');
      searchDrawer.setAttribute('aria-hidden', 'false');
      if (searchInput) {
        setTimeout(function () { searchInput.focus(); }, 50);
      }
    }

    function closeSearch() {
      searchDrawer.classList.remove('open');
      searchDrawer.setAttribute('aria-hidden', 'true');
    }

    searchToggles.forEach(function (toggle) {
      toggle.addEventListener('click', function (e) {
        e.preventDefault();
        const isOpen = searchDrawer.classList.contains('open');
        if (isOpen) { closeSearch(); } else { openSearch(); }
      });
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && searchDrawer.classList.contains('open')) {
        closeSearch();
      }
    });
  })();

  /* =========================================================================
     6. AOS Init
     AOS is enqueued via asset_url in theme.liquid.
     We wait for DOMContentLoaded; if AOS is present, initialise it.
     ========================================================================= */
  document.addEventListener('DOMContentLoaded', function () {
    if (typeof AOS !== 'undefined') {
      AOS.init({
        once:     true,
        offset:   60,
        easing:   'ease-out-quart',
        duration: 650,
      });
    }
  });

  /* =========================================================================
     7. Wishlist Toggle — persisted client-side via localStorage
     Stores an array of { handle, addedAt } objects under 'heidi-wishlist'
     so the heart state (and saved-date) survives reloads/navigation, and so
     sections/section-page-wishlist.liquid can read it back and render cards.
     ========================================================================= */
  (function initWishlist() {
    const STORAGE_KEY = 'heidi-wishlist';

    function readList() {
      try {
        const raw = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
        return Array.isArray(raw) ? raw : [];
      } catch (e) {
        return [];
      }
    }

    function writeList(list) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
      } catch (e) {
        /* localStorage unavailable (private mode / quota) — fail silently */
      }
    }

    function has(handle) {
      return readList().some(function (item) { return item.handle === handle; });
    }

    function add(handle) {
      const list = readList();
      if (!list.some(function (item) { return item.handle === handle; })) {
        list.push({ handle: handle, addedAt: Date.now() });
        writeList(list);
      }
    }

    function remove(handle) {
      writeList(readList().filter(function (item) { return item.handle !== handle; }));
    }

    function syncButton(btn) {
      const handle = btn.getAttribute('data-product-handle');
      if (!handle) return;
      const active = has(handle);
      btn.setAttribute('aria-pressed', String(active));
      btn.innerHTML = active ? '&#x2665;' : '&#x2661;';
      const label = btn.getAttribute('aria-label') || '';
      if (active && label.indexOf('Remove') === -1) {
        btn.setAttribute('aria-label', label.replace('Add', 'Remove').replace(' to wishlist', ' from wishlist'));
      } else if (!active && label.indexOf('Add') === -1) {
        btn.setAttribute('aria-label', label.replace('Remove', 'Add').replace(' from wishlist', ' to wishlist'));
      }
    }

    function syncAllButtons() {
      document.querySelectorAll('.product-wishlist[data-product-handle]').forEach(syncButton);
    }

    document.addEventListener('click', function (e) {
      const btn = e.target.closest('.product-wishlist');
      if (!btn) return;

      const handle = btn.getAttribute('data-product-handle');
      const wasPressed = btn.getAttribute('aria-pressed') === 'true';

      if (handle) {
        if (wasPressed) { remove(handle); } else { add(handle); }
      }

      btn.setAttribute('aria-pressed', String(!wasPressed));
      btn.innerHTML = wasPressed ? '&#x2661;' : '&#x2665;';

      document.dispatchEvent(new CustomEvent('wishlist:updated', {
        bubbles: true,
        detail: { handle: handle, active: !wasPressed },
      }));
    });

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', syncAllButtons);
    } else {
      syncAllButtons();
    }

    // Expose a small API so other scripts (e.g. the Wishlist page section)
    // can read/mutate the same storage without re-implementing this logic.
    window.__hlp = window.__hlp || {};
    window.__hlp.wishlist = {
      key: STORAGE_KEY,
      getAll: readList,
      has: has,
      add: add,
      remove: remove,
      clear: function () { writeList([]); },
      syncAllButtons: syncAllButtons,
    };
  })();

  /* =========================================================================
     8. Filter Tabs (collection page)
     ========================================================================= */
  (function initFilterTabs() {
    const filterBar = document.querySelector('.filter-bar');
    if (!filterBar) return;

    filterBar.addEventListener('click', function (e) {
      const tab = e.target.closest('.filter-tab[data-filter]');
      if (!tab) return;

      filterBar.querySelectorAll('.filter-tab').forEach(function (t) {
        t.classList.remove('active');
        t.setAttribute('aria-current', 'false');
      });

      tab.classList.add('active');
      tab.setAttribute('aria-current', 'true');
    });
  })();

  /* =========================================================================
     9. Toast notification helper
     ========================================================================= */
  window.__hlp = window.__hlp || {};

  window.__hlp.showToast = function (message, type) {
    type = type || 'default'; // 'success' | 'error' | 'default'

    let container = document.querySelector('.toast-container');
    if (!container) {
      container = document.createElement('div');
      container.className = 'toast-container';
      document.body.appendChild(container);
    }

    const toast = document.createElement('div');
    toast.className = 'toast' + (type !== 'default' ? ' toast--' + type : '');
    toast.textContent = message;
    toast.setAttribute('role', 'status');
    toast.setAttribute('aria-live', 'polite');
    container.appendChild(toast);

    setTimeout(function () {
      toast.style.animation = 'toastIn 0.3s var(--ease-out) reverse forwards';
      setTimeout(function () { toast.remove(); }, 350);
    }, 3500);
  };

  /* =========================================================================
     10. Quantity selector (used on PDP and in cart drawer)
     ========================================================================= */
  (function initQuantitySelectors() {
    document.addEventListener('click', function (e) {
      const btn = e.target.closest('.quantity-selector__btn');
      if (!btn) return;

      const selector = btn.closest('.quantity-selector');
      if (!selector) return;

      const input     = selector.querySelector('.quantity-selector__input');
      if (!input) return;

      const step      = parseInt(input.step, 10) || 1;
      const min       = parseInt(input.min,  10) || 1;
      const max       = parseInt(input.max,  10) || Infinity;
      let   val       = parseInt(input.value, 10) || min;

      if (btn.dataset.action === 'decrease') {
        val = Math.max(min, val - step);
      } else if (btn.dataset.action === 'increase') {
        val = Math.min(max, val + step);
      }

      input.value = val;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  })();

})();
